import React, { useState } from 'react';
import { Modal, Button, Input, InputNumber, message } from 'antd';
import { Container } from '@/components/ui/Container';
import StatusBadge from '@/components/ui/StatusBadge';
import { DetailLabel, DetailValue, DetailValueInherit } from './style';

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
        return new Date(dateStr).toLocaleString();
    } catch {
        return dateStr;
    }
};

/**
 * Content for the billing request side panel. Use inside <SidePanel>.
 * Receives the selected request from list (no separate detail API).
 * If user has billing.requests.respond and status is pending, shows Accept/Decline.
 */
const BillingRequestDetailPanel = ({ request, canRespond, respondMutation, onRespondSuccess }) => {
    const [acceptModalOpen, setAcceptModalOpen] = useState(false);
    const [declineModalOpen, setDeclineModalOpen] = useState(false);
    const [rating, setRating] = useState(5);
    const [declineNote, setDeclineNote] = useState('');

    if (!request) {
        return (
            <Container $marginBottom="24px">
                <DetailValueInherit>Select a request to view details.</DetailValueInherit>
            </Container>
        );
    }

    const isPending = (request.status || '').toLowerCase() === 'pending';

    const handleAccept = () => {
        respondMutation.mutate(
            { requestId: request.id, body: { action: 'accepted', rating } },
            {
                onSuccess: () => {
                    message.success('Payment request accepted.');
                    setAcceptModalOpen(false);
                    onRespondSuccess?.();
                },
                onError: (err) => {
                    message.error(err?.response?.data?.message || 'Failed to accept.');
                },
            },
        );
    };

    const handleDecline = () => {
        respondMutation.mutate(
            { requestId: request.id, body: { action: 'declined', note: declineNote || undefined } },
            {
                onSuccess: () => {
                    message.success('Payment request declined.');
                    setDeclineModalOpen(false);
                    setDeclineNote('');
                    onRespondSuccess?.();
                },
                onError: (err) => {
                    message.error(err?.response?.data?.message || 'Failed to decline.');
                },
            },
        );
    };

    return (
        <>
            <Container $marginBottom="24px" $display="flex" $alignItems="flex-start" $gap="12px">
                <DetailLabel>Project / Audit :</DetailLabel>
                <DetailValue>{request.project?.name ?? request.audit?.name ?? '—'}</DetailValue>
            </Container>
            <Container $marginBottom="24px" $display="flex" $alignItems="flex-start" $gap="12px">
                <DetailLabel>Requested by :</DetailLabel>
                <DetailValue>{request.requestedByUser?.name ?? request.requestedByUser?.email ?? '—'}</DetailValue>
            </Container>
            <Container $marginBottom="24px" $display="flex" $alignItems="flex-start" $gap="12px">
                <DetailLabel>Requested at :</DetailLabel>
                <DetailValue>{formatDate(request.requestedAt)}</DetailValue>
            </Container>
            <Container $marginBottom="24px" $display="flex" $alignItems="flex-start" $gap="12px">
                <DetailLabel>Status :</DetailLabel>
                <DetailValue>
                    <StatusBadge status={request.status} />
                </DetailValue>
            </Container>
            {request.clientRespondedAt && (
                <Container $marginBottom="24px" $display="flex" $alignItems="flex-start" $gap="12px">
                    <DetailLabel>Responded at :</DetailLabel>
                    <DetailValue>{formatDate(request.clientRespondedAt)}</DetailValue>
                </Container>
            )}
            {request.clientResponse && (
                <Container $marginBottom="24px" $display="flex" $alignItems="flex-start" $gap="12px">
                    <DetailLabel>Response :</DetailLabel>
                    <DetailValue>
                        {(request.clientResponse || '').toLowerCase() === 'accepted' ? 'Accepted' : 'Declined'}
                    </DetailValue>
                </Container>
            )}
            {request.clientNote && (
                <Container $marginBottom="24px" $display="flex" $flexDirection="column" $gap="8px">
                    <DetailLabel>Client note :</DetailLabel>
                    <DetailValue>{request.clientNote}</DetailValue>
                </Container>
            )}
            {request.clientRating != null && (
                <Container $marginBottom="24px" $display="flex" $alignItems="flex-start" $gap="12px">
                    <DetailLabel>Rating :</DetailLabel>
                    <DetailValue>{request.clientRating}/5</DetailValue>
                </Container>
            )}
            <Container $marginBottom="24px" $display="flex" $alignItems="flex-start" $gap="12px">
                <DetailLabel>Created at :</DetailLabel>
                <DetailValue>{formatDate(request.createdAt)}</DetailValue>
            </Container>

            {canRespond && isPending && (
                <Container $marginTop="24px" $display="flex" $gap="12px" $flexWrap="wrap">
                    <Button type="primary" onClick={() => setAcceptModalOpen(true)} loading={respondMutation.isPending}>
                        Accept
                    </Button>
                    <Button danger onClick={() => setDeclineModalOpen(true)} loading={respondMutation.isPending}>
                        Decline
                    </Button>
                </Container>
            )}

            <Modal
                maskClosable={false}
                title="Accept payment request"
                open={acceptModalOpen}
                onCancel={() => setAcceptModalOpen(false)}
                onOk={handleAccept}
                okText="Accept"
                confirmLoading={respondMutation.isPending}
            >
                <Container $display="flex" $flexDirection="column" $gap="12px" $paddingTop="8px">
                    <DetailLabel>Rating (optional, 1–5) :</DetailLabel>
                    <InputNumber min={1} max={5} value={rating} onChange={setRating} style={{ width: '100%' }} />
                </Container>
            </Modal>
            <Modal
                maskClosable={false}
                title="Decline payment request"
                open={declineModalOpen}
                onCancel={() => {
                    setDeclineModalOpen(false);
                    setDeclineNote('');
                }}
                onOk={handleDecline}
                okText="Decline"
                confirmLoading={respondMutation.isPending}
            >
                <Container $display="flex" $flexDirection="column" $gap="12px" $paddingTop="8px">
                    <DetailLabel>Note (optional) :</DetailLabel>
                    <Input.TextArea
                        rows={3}
                        value={declineNote}
                        onChange={(e) => setDeclineNote(e.target.value)}
                        placeholder="Reason for declining..."
                    />
                </Container>
            </Modal>
        </>
    );
};

export default BillingRequestDetailPanel;
